import React, { useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { UserListItem } from '../molecules/UserListItem';
import { ListViewShell } from '../../../../shared/components/ui/ListViewShell';
import type { UserStatus } from '../../api/userManagementApi';

export interface UserItem {
  id: string;
  fullName: string;
  email: string;
  joinedAt: string;
  roles: string[];
  status?: UserStatus;
}

export interface UsersListViewProps {
  users: UserItem[];
  loading: boolean;
  onOpenMenu: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  onItemClick?: (id: string) => void;
}

export const UsersListView: React.FC<UsersListViewProps> = ({
  users,
  loading,
  onOpenMenu,
  onItemClick,
}) => {
  const { activeUsers, offboardedUsers } = useMemo(() => {
    const active: UserItem[] = [];
    const offboarded: UserItem[] = [];
    for (const user of users) {
      if (user.status === 'Inactive') {
        offboarded.push(user);
      } else {
        active.push(user);
      }
    }
    return { activeUsers: active, offboardedUsers: offboarded };
  }, [users]);

  return (
    <ListViewShell
      itemCount={users.length}
      loading={loading}
      emptyMessage="No users found"
      data-testid="users-table"
      columnHeaders={
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Name
        </Typography>
      }
    >
      {activeUsers.map(user => (
        <UserListItem
          key={user.id}
          id={user.id}
          fullName={user.fullName}
          email={user.email}
          roles={user.roles}
          status={user.status}
          loading={loading}
          onOpenMenu={onOpenMenu}
          onRowClick={onItemClick}
        />
      ))}
      {offboardedUsers.length > 0 && (
        <>
          <Divider>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Offboarded
              </Typography>
            </Box>
          </Divider>
          {offboardedUsers.map(user => (
            <UserListItem
              key={user.id}
              id={user.id}
              fullName={user.fullName}
              email={user.email}
              roles={user.roles}
              status={user.status}
              loading={loading}
              onOpenMenu={onOpenMenu}
              onRowClick={onItemClick}
            />
          ))}
        </>
      )}
    </ListViewShell>
  );
};
